import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconTrash, IconX } from "@tabler/icons-react";
import Toggle from "Components/Admin/AdminSettings/Notification/Toggle";
import { useEcomConfig } from "hooks/useEcomConfig";
import {
  useGetLoyaltyConfigQuery,
  useSaveLoyaltyConfigMutation,
} from "features/admin/admin.api";
import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Select from "react-select";
import { useGetAllAdminProductsQuery } from "Services/Admin/ProductAdminApiSlice";
import { useAppSelector } from "hooks";
import z from "zod";

const loyaltyByPricesSchema = z
  .object({
    id: z.number().optional(),
    price: z.coerce.number().optional(),
    points: z.coerce.number().optional(),
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.fromDate && val.toDate) {
      const fromDate = new Date(val.fromDate);
      const toDate = new Date(val.toDate);
      if (toDate < fromDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["toDate"],
          message: "To date cannot be before From date",
        });
      }
    }
  });

const loyaltyByProductsSchema = z
  .object({
    id: z.coerce.number().optional(),
    item: z.string().optional(),
    points: z.coerce.number().optional(),
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.fromDate && val.toDate) {
      const fromDate = new Date(val.fromDate);
      const toDate = new Date(val.toDate);
      if (toDate < fromDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["toDate"],
          message: "To date cannot be before From date",
        });
      }
    }
  });

export const loyaltyPointsSchema = z
  .object({
    id: z.coerce.number().optional(),
    title: z
      .string()
      .min(1, { message: "Title is required" })
      .max(100, { message: "Title cannot exceed 100 characters" }),
    description: z.string().optional(),

    byPriceEnabled: z.coerce.boolean().default(false),
    byPriceScheduled: z.coerce.boolean().default(false),
    loyaltyByPrices: z
      .array(loyaltyByPricesSchema)
      .superRefine((arr, ctx) => {
        for (let i = 0; i < arr.length; i++) {
          for (let j = i + 1; j < arr.length; j++) {
            const a = arr[i];
            const b = arr[j];
            if (a.fromDate && a.toDate && b.fromDate && b.toDate) {
              const fromDateA = new Date(a.fromDate);
              const toDateA = new Date(a.toDate);
              const fromDateB = new Date(b.fromDate);
              const toDateB = new Date(b.toDate);

              const overlap = fromDateA <= toDateB && fromDateB <= toDateA;
              if (overlap) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [j],
                  message: `Date range at row ${j + 1} overlaps with row ${i + 1
                    }`,
                });
              }
            }
          }
        }
      })
      .optional(),

    byProductEnabled: z.coerce.boolean().default(false),
    byProductScheduled: z.coerce.boolean().default(false),
    loyaltyByProducts: z
      .array(loyaltyByProductsSchema)
      .superRefine((arr, ctx) => {
        for (let i = 0; i < arr.length; i++) {
          for (let j = i + 1; j < arr.length; j++) {
            const a = arr[i];
            const b = arr[j];
            if (
              a.item === b.item &&
              a.fromDate &&
              a.toDate &&
              b.fromDate &&
              b.toDate
            ) {
              const fromDateA = new Date(a.fromDate);
              const toDateA = new Date(a.toDate);
              const fromDateB = new Date(b.fromDate);
              const toDateB = new Date(b.toDate);

              const overlap = fromDateA <= toDateB && fromDateB <= toDateA;
              if (overlap) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [j],
                  message: `Date range for product "${a.item}" at row ${j + 1
                    } overlaps with row ${i + 1}`,
                });
              }
            }
          }
        }
      })
      .optional(),

    redeemPoints: z
      .coerce.number()
      .min(1, { message: "Redeem Points value should be minimum 1" }),
    redeemPrice: z
      .coerce.number()
      .min(1, { message: "Redeem Price value should be minimum 1" }),
    returnPoints: z
      .coerce.number()
      .min(1, { message: "Return Points value should be minimum 1" }),
    returnPrice: z
      .coerce.number()
      .min(1, { message: "Return Price value should be minimum 1" }),
    maxRedeem: z
      .coerce.number()
      .min(1, { message: "Max Redeem must be at least 1" })
      .optional(),

    pointsUponRegister: z.boolean(),
    uponRegisterPoints: z.coerce.number().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.byPriceEnabled) {
      if (!data.loyaltyByPrices || data.loyaltyByPrices.length === 0) {
        ctx.addIssue({
          path: ["loyaltyByPrices"],
          code: "custom",
          message: "Loyalty By Prices is required when By Price is enabled",
        });
      }
    }

    if (data.byPriceScheduled && data.loyaltyByPrices) {
      data.loyaltyByPrices.forEach((row, idx) => {
        if (
          (row.price === undefined || row.price === null || row.price === 0) &&
          (row.points === undefined || row.points === null || row.points === 0)
        ) {
          return;
        }

        if (!row.fromDate || !row.toDate) {
          ctx.addIssue({
            path: ["loyaltyByPrices", idx],
            code: "custom",
            message:
              "From Date and To Date are required when By Price scheduling is enabled",
          });
        }
      });
    }

    if (data.byPriceEnabled && data.loyaltyByPrices) {
      data.loyaltyByPrices.forEach((row, idx) => {
        // Ignore completely empty rows
        if (
          (row.price === undefined || row.price === null || row.price === 0) &&
          (row.points === undefined || row.points === null || row.points === 0)
        ) {
          return;
        }
        if (row.points === undefined || row.points === null || row.points < 1) {
          ctx.addIssue({
            path: ["loyaltyByPrices", idx, "points"],
            code: "custom",
            message: "Points value should be minimum 1",
          });
        }

        if (!row.price || row.price < 1) {
          ctx.addIssue({
            path: ["loyaltyByPrices", idx, "price"],
            code: "custom",
            message: "Price value should be minimum 1",
          });
        }
      });
    }

    if (data.byProductEnabled) {
      if (!data.loyaltyByProducts || data.loyaltyByProducts.length === 0) {
        ctx.addIssue({
          path: ["loyaltyByProducts"],
          code: "custom",
          message: "Loyalty By Products is required when By Product is enabled",
        });
      }
    }

    if (data.byProductScheduled && data.loyaltyByProducts) {
      data.loyaltyByProducts.forEach((row, idx) => {
        if (!row.fromDate || !row.toDate) {
          ctx.addIssue({
            path: ["loyaltyByProducts", idx],
            code: "custom",
            message:
              "From Date and To Date are required when By Product scheduling is enabled",
          });
        }
      });
    }

    if (data.byProductEnabled && data.loyaltyByProducts) {
      data.loyaltyByProducts.forEach((row, idx) => {
        if (!row.item || row.item.trim() === "") {
          ctx.addIssue({
            path: ["loyaltyByProducts", idx, "item"],
            code: "custom",
            message: "Product is required when By Product is enabled",
          });
        }
        if (!row.points || row.points < 1) {
          ctx.addIssue({
            path: ["loyaltyByProducts", idx, "points"],
            code: "custom",
            message:
              "Points value should be minimum 1 when By Product is enabled",
          });
        }
      });
    }
  });

// type LoyaltyPointsForm = z.infer<typeof loyaltyPointsSchema>;

const AdminLoyaltyPoints = () => {
  const { config, hasChanges } = useAppSelector(
    (state: any) => state.configuration,
  );

  const [byPrice, setByPrice] = useState<{
    enabled: boolean;
    scheduled: boolean;
  }>({ enabled: false, scheduled: false });
  const [byProduct, setByProduct] = useState<{
    enabled: boolean;
    scheduled: boolean;
  }>({ enabled: false, scheduled: false });

  const { data: productsData, isLoading: productsLoading } =
    useGetAllAdminProductsQuery({
      category: undefined,
      pageSize: 100,
      activePage: 1,
      subCategory: undefined,
      brand: undefined,
    });

  const [saveLoyaltyConfig, { isLoading: saveLoyaltyConfigLoading }] =
    useSaveLoyaltyConfigMutation();


  const { isLoyaltyPointsEnabled } = useEcomConfig();
  const submitLockRef = useRef(false);
  const {
    data: loyaltyConfigData,
    isLoading: loyaltyConfigLoading,
    isFetching: loyaltyConfigFetching,
  } = useGetLoyaltyConfigQuery(undefined, { skip: !isLoyaltyPointsEnabled });

  const productOptions =
    productsData?.products?.map((product) => ({
      value: product.product.item,
      label: `${product.product.item} - ${product.product.itemDesc}`,
    })) || [];

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    trigger,
  } = useForm({
    resolver: zodResolver(loyaltyPointsSchema),
    defaultValues: {
      id: 0,
      title: "",
      description: "",
      byPriceEnabled: false,
      byPriceScheduled: false,
      loyaltyByPrices: [],
      byProductEnabled: false,
      byProductScheduled: false,
      loyaltyByProducts: [],
      redeemPoints: undefined,
      redeemPrice: undefined,
      returnPoints: undefined,
      returnPrice: undefined,
      maxRedeem: undefined,
      pointsUponRegister: false,
      uponRegisterPoints: undefined,
    },
  });

  const {
    fields: byPriceFields,
    append: addByPriceRow,
    remove: removeByPriceRow,
  } = useFieldArray({ control, name: "loyaltyByPrices" });

  useEffect(() => {
    if (byPrice.enabled && byPriceFields.length === 0) {
      addByPriceRow({
        price: undefined,
        points: undefined,
        fromDate: "",
        toDate: "",
      });
    }
  }, [byPrice.enabled]);

  const {
    fields: byProductFields,
    append: addByProductRow,
    remove: removeByProductRow,
  } = useFieldArray({ control, name: "loyaltyByProducts" });

  useEffect(() => {
    if (byProduct.enabled && byProductFields.length === 0) {
      addByProductRow({
        item: "",
        points: undefined,
        fromDate: "",
        toDate: "",
      });
    }
  }, [byProduct.enabled, byProductFields.length]);

  useEffect(() => {
    if (loyaltyConfigData?.results) {
      const data = loyaltyConfigData.results;

      reset({
        id: data.id,
        title: data.title,
        description: data.description,
        byPriceEnabled: data.byPriceEnabled,
        byPriceScheduled: data.byPriceScheduled,
        loyaltyByPrices:
          data.loyaltyByPrices?.map((item: any) => ({
            ...item,
            fromDate: item.fromDate?.split("T")[0],
            toDate: item.toDate?.split("T")[0],
          })) ?? [],

        byProductEnabled: data.byProductEnabled,
        byProductScheduled: data.byProductScheduled,
        loyaltyByProducts:
          data.loyaltyByProducts?.map((item: any) => ({
            ...item,
            fromDate: item.fromDate?.split("T")[0],
            toDate: item.toDate?.split("T")[0],
          })) ?? [],

        redeemPoints: data.redeemPoints,
        redeemPrice: data.redeemPrice,
        returnPoints: data.returnPoints,
        returnPrice: data.returnPrice,
        maxRedeem: data.maxRedeem,
        pointsUponRegister: data.pointsUponRegister,
        uponRegisterPoints: data.uponRegisterPoints,
      });

      setByPrice({
        enabled: data.byPriceEnabled,
        scheduled: data.byPriceScheduled,
      });

      setByProduct({
        enabled: data.byProductEnabled,
        scheduled: data.byProductScheduled,
      });
    }
  }, [loyaltyConfigData, reset]);


  // useEffect(() => {
  //   if (config?.loyaltyPoints) {
  //     reset(config.loyaltyPoints);
  //   }
  // }, [config, reset]);

  const onSubmit = async (data: z.infer<typeof loyaltyPointsSchema>) => {

    if (submitLockRef.current) return;

    submitLockRef.current = true;

    try {
      const existingData = loyaltyConfigData?.results;

      const apiData: any = {
        plant: existingData?.plant || "C2620614616S2T",
        id: data.id,
        title: data.title,
        description: data.description,
        byPriceEnabled: data.byPriceEnabled,
        byPriceScheduled: data.byPriceScheduled,
        byProductEnabled: data.byProductEnabled,
        byProductScheduled: data.byProductScheduled,
        redeemPoints: data.redeemPoints,
        redeemPrice: data.redeemPrice,
        returnPoints: data.returnPoints,
        returnPrice: data.returnPrice,
        maxRedeem: data.maxRedeem,
        pointsUponRegister: data.pointsUponRegister,
        uponRegisterPoints: data.uponRegisterPoints,
      };

      if (data.byPriceEnabled && data.loyaltyByPrices) {
        apiData.loyaltyByPrices = data.loyaltyByPrices.map((item) => ({
          id: item.id,
          price: item.price,
          points: item.points,
          fromDate: item.fromDate ? new Date(item.fromDate) : undefined,
          toDate: item.toDate ? new Date(item.toDate) : undefined,
        }));
      } else {
        apiData.loyaltyByPrices = [];
      }

      if (data.byProductEnabled && data.loyaltyByProducts) {
        apiData.loyaltyByProducts = data.loyaltyByProducts.map((item) => ({
          id: item.id,
          item: item.item,
          points: item.points,
          fromDate: item.fromDate ? new Date(item.fromDate) : undefined,
          toDate: item.toDate ? new Date(item.toDate) : undefined,
        }));
      } else {
        apiData.loyaltyByProducts = [];
      }

      const res = await saveLoyaltyConfig({ data: apiData }).unwrap();

      notifications.show({
        id: "loyalty-save-success",
        message: res.message,
        color: "green",
        icon: <IconCheck size="1rem" />,
        autoClose: 4000,
      });

    } catch (err: any) {
      notifications.show({
        id: "loyalty-save-error",
        message: err?.data?.message || "An error occurred",
        color: "red",
        icon: <IconX size="1rem" />,
        autoClose: 6000,
      });
    } finally {
      submitLockRef.current = false;
    }
  };
  const getBlockedDateRangeForProduct = (
    productCode: string | undefined,
    currentIndex: number
  ) => {
    const rows = watch("loyaltyByProducts") || [];

    if (!productCode) return undefined;

    const existingRanges = rows
      .filter((row, idx) => idx !== currentIndex && row.item === productCode)
      .map((row) => ({
        from: row.fromDate,
        to: row.toDate,
      }))
      .filter((r) => r.from && r.to);

    return existingRanges;
  };
  if (loyaltyConfigLoading || loyaltyConfigFetching) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  return (
    <form
      onSubmit={handleSubmit(
        onSubmit,
        (errors) => {
        }
      )}
      className="px-3 py-4 pb-32 sm:p-6 max-w-7xl mx-auto overflow-visible font-body"
    >


      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Loyalty Points</h1>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saveLoyaltyConfigLoading}
            className={`px-4 py-2 text-sm text-white rounded-md flex items-center gap-2
    ${saveLoyaltyConfigLoading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            {saveLoyaltyConfigLoading && (
              <svg
                className="w-4 h-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            )}
            {saveLoyaltyConfigLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      <div className="mt-6 bg-white rounded-lg border shadow-sm overflow-visible">

        <div className="p-6 overflow-visible">

          <div className="space-y-8">
            <div>
              <h2 className="text-sm font-medium">Basic Details</h2>
              <p className="text-xs text-gray-500 mt-1">
                Enter your loyalty points information
              </p>

              <div className="grid grid-cols-1 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    {...register("title")}
                    className="w-full px-3 py-2 border rounded-md text-xs"
                    placeholder="Enter title"
                  />
                  <p className="text-xs text-red-500">
                    {errors.title?.message}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    placeholder="Enter Description"
                    {...register("description")}
                    className="w-full px-3 py-2 border rounded-md text-sm h-32"
                    maxLength={100}
                  />
                  <p className="text-xs text-red-500">
                    {errors.description?.message}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-sm font-medium">By Price</h2>

                  <p className="text-xs text-gray-500 mt-1">
                    Set Loyalty Points by Price
                  </p>
                </div>
                <div>
                  <Controller
                    name="byPriceEnabled"
                    control={control}
                    render={({ field }) => (
                      <Toggle
                        enabled={!!field.value}
                        onChange={(value) => {
                          setByPrice((prev) => ({ ...prev, enabled: value }));
                          field.onChange(value);

                          if (value && byPriceFields.length === 0) {
                            addByPriceRow({
                              price: undefined,
                              points: undefined,
                              fromDate: "",
                              toDate: "",
                            });
                          }

                          if (!value) {
                            setValue("loyaltyByPrices", []);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>

              {byPrice.enabled ? (
                <div>
                  <div className="flex items-center my-6">
                    <Controller
                      name="byPriceScheduled"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={Boolean(field.value)}
                          onChange={() => {
                            setByPrice((prev) => ({
                              ...prev,
                              scheduled: !prev.scheduled,
                            }));
                            field.onChange(!field.value);
                          }}
                          className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                      )}
                    />

                    <label
                      htmlFor="byPriceScheduled"
                      className="ml-2 block text-xs text-gray-700"
                    >
                      Scheduled
                    </label>
                  </div>

                  {!byPrice.scheduled ? (
                    <div className="w-full overflow-x-auto">
                      <table className="min-w-full border border-gray-200 text-sm text-left">
                        <thead className="bg-gray-100 text-gray-600 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border">Price</th>
                            <th className="px-4 py-2 border">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byPriceFields.map((field, idx) => (
                            <tr key={field.id} className="bg-white">
                              <td className="px-4 py-2 border">
                                <input
                                  type="number"
                                  {...register(`loyaltyByPrices.${idx}.price`, {
                                    valueAsNumber: true,
                                  })}
                                  className="border px-2 py-1 rounded w-full text-left"
                                  placeholder="Enter Price"
                                  maxLength={8}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="number"
                                  {...register(
                                    `loyaltyByPrices.${idx}.points`,
                                    {
                                      valueAsNumber: true,
                                    },
                                  )}
                                  className="border px-2 py-1 rounded w-full text-left"
                                  placeholder="Enter Points"
                                  maxLength={8}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                {idx > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => removeByPriceRow(idx)}
                                    className="text-red-500 text-xs"
                                  >
                                    <IconTrash />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <table className="min-w-full border border-gray-200 text-sm text-left">
                        <thead className="bg-gray-100 text-gray-600 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border">Price</th>
                            <th className="px-4 py-2 border">Points</th>
                            <th className="px-4 py-2 border">From Date</th>
                            <th className="px-4 py-2 border">To Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byPriceFields.map((field, idx) => (
                            <tr key={field.id} className="bg-white">
                              <td className="px-4 py-2 border">
                                <input
                                  type="number"
                                  {...register(`loyaltyByPrices.${idx}.price`, {
                                    valueAsNumber: true,
                                  })}
                                  className="border px-2 py-1 rounded w-full text-left"
                                  placeholder="Enter Price"
                                  maxLength={8}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="number"
                                  {...register(
                                    `loyaltyByPrices.${idx}.points`,
                                    {
                                      valueAsNumber: true,
                                    },
                                  )}
                                  className="border px-2 py-1 rounded w-full text-left"
                                  placeholder="Enter Points"
                                  maxLength={8}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="date"
                                  {...register(`loyaltyByPrices.${idx}.fromDate`, {
                                    onChange: () => {
                                      trigger(`loyaltyByPrices.${idx}`);
                                    },
                                  })}
                                  min={
                                    idx > 0
                                      ? watch(
                                        `loyaltyByPrices.${idx - 1}.toDate`,
                                      ) || undefined
                                      : undefined
                                  }
                                  className="w-full px-3 py-2 border rounded-md text-xs"
                                  placeholder="Select From Date"
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="date"
                                  {...register(`loyaltyByPrices.${idx}.toDate`, {
                                    onChange: () => {
                                      trigger(`loyaltyByPrices.${idx}`);
                                    },
                                  })}
                                  min={
                                    watch(`loyaltyByPrices.${idx}.fromDate`) ||
                                    undefined
                                  }
                                  className="w-full px-3 py-2 border rounded-md text-xs"
                                  placeholder="Select To Date"
                                />
                              </td>
                              {idx > 0 ? (
                                <td className="px-4 py-2 border">
                                  <button
                                    type="button"
                                    onClick={() => removeByPriceRow(idx)}
                                    className="text-red-500 text-xs"
                                  >
                                    <IconTrash />
                                  </button>
                                </td>
                              ) : (
                                <></>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {Array.isArray(errors.loyaltyByPrices) &&
                    errors.loyaltyByPrices?.map((err: any, index: any) => (
                      <div key={index} className="text-xs text-red-500">
                        {err?.price?.message && (
                          <p>Price: {err.price.message}</p>
                        )}
                        {err?.points?.message && (
                          <p>Points: {err.points.message}</p>
                        )}
                        {err?.fromDate?.message && (
                          <p>From Date: {err.fromDate.message}</p>
                        )}
                        {err?.toDate?.message && (
                          <p>To Date: {err.toDate.message}</p>
                        )}
                        {err?.message && <p>{err.message}</p>}
                      </div>
                    ))}

                  <div className="text-blue-600 pt-3 px-2">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        addByPriceRow({
                          price: undefined,
                          points: undefined,
                          fromDate: "",
                          toDate: "",
                        });
                      }}
                    >
                      + Add Line
                    </button>
                  </div>

                </div>
              ) : (
                <></>
              )}
            </div>

            <div className="border-t pt-6">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-sm font-medium">By Product</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Set Loyalty Points by product
                  </p>
                </div>
                <div>
                  <Controller
                    name="byProductEnabled"
                    control={control}
                    render={({ field }) => (
                      <Toggle
                        enabled={!!field.value}
                        onChange={(value) => {
                          setByProduct((prev) => ({ ...prev, enabled: value }));
                          field.onChange(value);

                          if (value && byProductFields.length === 0) {
                            addByProductRow({
                              item: "",
                              points: undefined,
                              fromDate: "",
                              toDate: "",
                            });
                          }

                          if (!value) {
                            setValue("loyaltyByProducts", []);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>

              {byProduct.enabled ? (
                <div>
                  <div className="flex items-center my-6">
                    <Controller
                      name="byProductScheduled"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="byProductScheduled"
                          type="checkbox"
                          checked={Boolean(field.value)}
                          onChange={() => {
                            setByProduct((prev) => ({
                              ...prev,
                              scheduled: !prev.scheduled,
                            }));
                            field.onChange(!field.value);
                          }}
                          className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                      )}
                    />

                    <label
                      htmlFor="byProductScheduled"
                      className="ml-2 block text-xs text-gray-700"
                    >
                      Scheduled
                    </label>
                  </div>
                  {!byProduct.scheduled ? (
                    <div className="w-full overflow-x-auto">
                      <table className="min-w-[640px] sm:min-w-full border border-gray-200 text-sm text-left">

                        <thead className="bg-gray-100 text-gray-600 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border">Price</th>
                            <th className="px-4 py-2 border">Points</th>
                            <th className="px-4 py-2 border"></th>
                          </tr>
                        </thead>

                        <tbody>
                          {byProductFields.map((field, idx) => (
                            <tr key={field.id} className="bg-white">
                              <td className="px-4 py-2 border">
                                <Controller
                                  name={`loyaltyByProducts.${idx}.item`}
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      options={productOptions}
                                      onChange={(newValue) =>
                                        field.onChange(newValue!.value)
                                      }
                                      value={
                                        productOptions.find(
                                          (o) => o.value === field.value,
                                        ) || null
                                      }
                                      placeholder="Select a Product..."
                                      isLoading={productsLoading}
                                      className="w-full basic-single"
                                      classNamePrefix="select"
                                      menuPortalTarget={document.body}
                                      menuPosition="fixed"
                                      components={{
                                        IndicatorsContainer: () => null,
                                      }}
                                      styles={{
                                        control: (base) => ({
                                          ...base,
                                          border: "1px solid #d1d5db",
                                          borderRadius: "0.375rem",
                                          minHeight: "auto",
                                          padding: "0.25rem",
                                          cursor: "pointer",
                                          "&:hover": { borderColor: "#d1d5db" },
                                        }),
                                        menuPortal: (base) => ({
                                          ...base,
                                          zIndex: 9999,
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          zIndex: 9999,
                                        }),
                                        dropdownIndicator: () => ({
                                          display: "none",
                                        }),

                                        indicatorSeparator: () => ({
                                          display: "none",
                                        }),
                                      }}
                                    />
                                  )}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="number"
                                  {...register(
                                    `loyaltyByProducts.${idx}.points`,

                                  )}
                                  className="border px-2 py-1 rounded w-full text-left"
                                  placeholder="Enter Points"
                                  maxLength={8}
                                />
                              </td>
                              {idx > 0 ? (
                                <td className="px-4 py-2 border">
                                  <button
                                    type="button"
                                    onClick={() => removeByProductRow(idx)}
                                    className="text-red-500 text-xs"
                                  >
                                    <IconTrash />
                                  </button>
                                </td>
                              ) : (
                                <></>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <table className="min-w-[640px] sm:min-w-full border border-gray-200 text-sm text-left">
                        <thead className="bg-gray-100 text-gray-600 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border w-[50%] sm:w-auto">Product</th>

                            <th className="px-4 py-2 border">Points</th>
                            <th className="px-4 py-2 border">From Date</th>
                            <th className="px-4 py-2 border">To Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byProductFields.map((field, idx) => (
                            <tr key={field.id} className="bg-white">
                              <td className="px-4 py-2 border">
                                <Controller
                                  name={`loyaltyByProducts.${idx}.item`}
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      options={productOptions}
                                      onChange={(newValue) =>
                                        field.onChange(newValue!.value)
                                      }
                                      value={
                                        productOptions.find(
                                          (o) => o.value === field.value,
                                        ) || null
                                      }
                                      placeholder="Select a Product..."
                                      isLoading={productsLoading}
                                      className="w-full basic-single"
                                      classNamePrefix="select"
                                      menuPortalTarget={document.body}
                                      menuPosition="fixed"
                                      components={{
                                        IndicatorsContainer: () => null,
                                      }}
                                      styles={{
                                        control: (base) => ({
                                          ...base,
                                          border: "1px solid #d1d5db",
                                          borderRadius: "0.375rem",
                                          minHeight: "auto",
                                          padding: "0.25rem",
                                          cursor: "pointer",
                                          "&:hover": { borderColor: "#d1d5db" },
                                        }),
                                        menuPortal: (base) => ({
                                          ...base,
                                          zIndex: 9999,
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          zIndex: 9999,
                                        }),
                                        dropdownIndicator: () => ({
                                          display: "none",
                                        }),
                                        indicatorSeparator: () => ({
                                          display: "none",
                                        }),
                                      }}
                                    />
                                  )}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="number"
                                  {...register(
                                    `loyaltyByProducts.${idx}.points`,

                                  )}
                                  className="border px-2 py-1 rounded w-full text-left"
                                  placeholder="Enter Points"
                                  maxLength={8}
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="date"
                                  {...register(`loyaltyByProducts.${idx}.fromDate`, {
                                    onChange: () => {
                                      trigger(`loyaltyByProducts.${idx}`);
                                    },
                                  })}
                                  min={(() => {
                                    const productCode = watch(`loyaltyByProducts.${idx}.item`);
                                    const blocked = getBlockedDateRangeForProduct(productCode, idx);

                                    if (!blocked || blocked.length === 0) return undefined;

                                    return blocked[blocked.length - 1]?.to;
                                  })()}
                                  className="w-full px-3 py-2 border rounded-md text-xs"
                                  placeholder="Select From Date"
                                />
                              </td>
                              <td className="px-4 py-2 border">
                                <input
                                  type="date"
                                  {...register(`loyaltyByProducts.${idx}.toDate`, {
                                    onChange: () => {
                                      trigger(`loyaltyByProducts.${idx}`);
                                    },
                                  })}
                                  min={
                                    watch(
                                      `loyaltyByProducts.${idx}.fromDate`,
                                    ) || undefined
                                  }
                                  className="w-full px-3 py-2 border rounded-md text-xs"
                                  placeholder="Select To Date"
                                />
                              </td>
                              {idx > 0 ? (
                                <td className="px-4 py-2 border">
                                  <button
                                    type="button"
                                    onClick={() => removeByProductRow(idx)}
                                    className="text-red-500 text-xs"
                                  >
                                    <IconTrash />
                                  </button>
                                </td>
                              ) : (
                                <></>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}{" "}
                  {Array.isArray(errors.loyaltyByProducts) &&
                    errors.loyaltyByProducts?.map((err: any, index: any) => (
                      <div key={index} className="text-xs text-red-500">
                        {err?.item?.message && <p>Item: {err.item.message}</p>}
                        {err?.points?.message && (
                          <p>Points: {err.points.message}</p>
                        )}
                        {err?.fromDate?.message && (
                          <p>From Date: {err.fromDate.message}</p>
                        )}
                        {err?.toDate?.message && (
                          <p>To Date: {err.toDate.message}</p>
                        )}
                        {err?.message && <p>{err.message}</p>}
                      </div>
                    ))}
                  <div className="text-blue-600 pt-3 px-2">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        addByProductRow({ item: "", points: undefined });
                      }}
                    >
                      + Add Line
                    </button>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>

            <div className="border-t pt-6">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-sm font-medium">Redeem</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Set Price for the Redeem Points
                  </p>
                </div>
              </div>

              <div className="my-3">
                <div className="w-full overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 font-semibold">
                      <tr>
                        <th className="px-4 py-2 border">Points</th>
                        <th className="px-4 py-2 border">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="px-4 py-2 border">
                          <input
                            type="number"
                            {...register("redeemPoints",)}
                            className="border px-2 py-1 rounded w-full text-left"
                            placeholder="Enter Points"
                            maxLength={8}
                          />
                          <p className="text-xs text-red-500">
                            {errors.redeemPoints?.message}
                          </p>
                        </td>
                        <td className="px-4 py-2 border">
                          <input
                            type="number"
                            {...register("redeemPrice",)}
                            className="border px-2 py-1 rounded w-full text-left"
                            placeholder="Enter Price"
                            maxLength={8}
                          />
                          <p className="text-xs text-red-500">
                            {errors.redeemPrice?.message}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Redeem Section
            <div className="border-t pt-6">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-sm font-medium">Redeem</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Set Price for the Redeem Points
                  </p>
                </div>
              </div>

              <div className="my-3">
                <table className="min-w-full border border-gray-200 text-sm text-left">
                <div className="w-full overflow-x-auto">
  <table className="min-w-full border border-gray-200 text-sm text-left">

                  <thead className="bg-gray-100 text-gray-600 font-semibold">
                    <tr>
                      <th className="px-4 py-2 border">Points</th>
                      <th className="px-4 py-2 border">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="px-4 py-2 border">
                        <input
                          type="number"
                          {...register("redeemPoints")}
                          className="border px-2 py-1 rounded w-full text-left"
                          placeholder="Enter Points"
                        />
                        <p className="text-xs text-red-500">
                          {errors.redeemPoints?.message}
                        </p>
                      </td>
                      <td className="px-4 py-2 border">
                        <input
                          type="number"
                          {...register("redeemPrice")}
                          className="border px-2 py-1 rounded w-full text-left"
                          placeholder="Enter Price"
                        />
                        <p className="text-xs text-red-500">
                          {errors.redeemPrice?.message}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div> */}

            <div className="border-t pt-6">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-sm font-medium">Return</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Set Price for the Return Points
                  </p>
                </div>
              </div>

              <div className="my-3">
                <div className="w-full overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 font-semibold">
                      <tr>
                        <th className="px-4 py-2 border">Points</th>
                        <th className="px-4 py-2 border">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="px-4 py-2 border">
                          <input
                            type="number"
                            {...register("returnPoints")}
                            className="border px-2 py-1 rounded w-full text-left"
                            placeholder="Enter Points"
                          />
                          <p className="text-xs text-red-500">
                            {errors.returnPoints?.message}
                          </p>
                        </td>
                        <td className="px-4 py-2 border">
                          <input
                            type="number"
                            {...register("returnPrice",)}
                            className="border px-2 py-1 rounded w-full text-left"
                            placeholder="Enter Price"
                          />
                          <p className="text-xs text-red-500">
                            {errors.returnPrice?.message}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h2 className="text-sm font-medium">Max Redeem</h2>
              <p className="text-xs text-gray-500 mt-1">
                Set maximum percentage user can claim from total price
              </p>

              <input
                type="number"
                {...register("maxRedeem",)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="Enter max redeem"
              />
              <p className="text-xs text-red-500">
                {errors.maxRedeem?.message}
              </p>
              <p className="text-xs text-green-500 pt-1">
                Note: Preferably set the maximum redeem percentage under 25%.
              </p>
            </div>

            <div className="border-t pt-6">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-sm font-medium">General Configuration</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Set Points for Customer Behavior
                  </p>
                </div>
              </div>

              <div className="my-3">
                <div className="w-full overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 font-semibold">
                      <tr>
                        <th className="px-4 py-2 border">Toggle</th>
                        <th className="px-4 py-2 border">Behavior</th>
                        <th className="px-4 py-2 border">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="px-4 py-2 border">
                          <Controller
                            name="pointsUponRegister"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={() => field.onChange(!field.value)}
                              />
                            )}
                          />
                        </td>
                        <td className="px-4 py-2 border">
                          Points Upon Customer Registration
                        </td>
                        <td className="px-4 py-2 border">
                          <input
                            type="number"
                            {...register("uponRegisterPoints",)}
                            className="border px-2 py-1 rounded w-full text-left"
                            placeholder="Enter Points"
                          />
                          <p className="text-xs text-red-500">
                            {errors.uponRegisterPoints?.message}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AdminLoyaltyPoints;
